import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  selectDisplayValueMode,
  selectFilteredSortedPlanProducts,
  selectForecastItems,
  selectPlannedItems,
  selectShipmentItems,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { isPreviousMonth } from '@/core/utils/isPreviousMonth.ts'
import { getPlanFormattedDateFromDate } from '@/core/utils/getPlanFormattedDateFromDate.ts'
import { Value, Wrapper } from './PlanTableMonthGroupCell.styles.ts'
import { formatNumber } from '@/core/utils/formatNumber.ts'
import { formatCurrency } from '@/core/utils/formatCurrency.ts'
import { DisplayValueMode } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import {
  shouldHighlightMonth,
  isCurrentMonth,
} from '@/core/utils/shouldHighlightMonth.ts'

const UNAVAILABLE_CODE = 'UNAVAILABLE'

interface Props {
  date: Date
  groupCode: string | null
  categoryCode: string | null
}

const PlanTableMonthGroupCell: FC<Props> = ({
  date,
  groupCode,
  categoryCode,
}) => {
  const forecastItems = useSelector(selectForecastItems)
  const plannedItems = useSelector(selectPlannedItems)
  const shipmentItems = useSelector(selectShipmentItems)

  const displayValueMode = useSelector(selectDisplayValueMode)

  const shouldHighlight = shouldHighlightMonth(date)
  const isPrevious = isPreviousMonth(date)
  const isCurrent = isCurrentMonth(date)

  const isUnavailableGroup =
    groupCode === UNAVAILABLE_CODE && categoryCode === UNAVAILABLE_CODE

  const allProducts = useSelector(selectFilteredSortedPlanProducts)

  const products = useMemo(() => {
    if (isUnavailableGroup) {
      return allProducts.filter((product) => product.unavailable)
    }

    return allProducts.filter((product) => {
      const productGroupCode = product.group?.code ?? null
      const productCategoryCode = product.category?.code ?? null

      return (
        productGroupCode === groupCode && productCategoryCode === categoryCode
      )
    })
  }, [allProducts, groupCode, categoryCode, isUnavailableGroup])

  const { amount, cost } = useMemo(() => {
    const planFormattedDate = getPlanFormattedDateFromDate(date)

    let amount = 0
    let cost = 0

    if (isPrevious) {
      for (const product of products) {
        const productShipmentItems = shipmentItems[product.id]

        if (!productShipmentItems) {
          continue
        }

        const monthShipments = productShipmentItems[planFormattedDate]

        if (monthShipments) {
          amount += monthShipments.reduce((acc, item) => acc + item.amount, 0)
          cost += monthShipments.reduce(
            (acc, item) => acc + item.amount * item.price,
            0,
          )
        }
      }
    } else {
      for (const product of products) {
        const productPlanItems = plannedItems[product.id]
        const productForecastItems = forecastItems[product.id]

        if (!productPlanItems && !productForecastItems) {
          continue
        }

        const productMonthPlanItem = productPlanItems
          ? productPlanItems[planFormattedDate]
          : null
        const productMonthForecastItem = productForecastItems
          ? productForecastItems[planFormattedDate]
          : null

        if (productMonthPlanItem) {
          amount += productMonthPlanItem.amount
          cost += productMonthPlanItem.amount * product.price
        } else if (productMonthForecastItem) {
          amount += productMonthForecastItem.amountSuggested
          cost += productMonthForecastItem.amountSuggested * product.price
        }
      }
    }

    return { amount, cost }
  }, [date, forecastItems, isPrevious, plannedItems, products, shipmentItems])

  return (
    <Wrapper className={shouldHighlight ? 'highlight' : ''}>
      <Value sx={{ fontWeight: isCurrent ? 700 : 400 }}>
        {displayValueMode === DisplayValueMode.amount
          ? formatNumber(amount)
          : formatCurrency(cost, { maximumFractionDigits: 0 })}
      </Value>
    </Wrapper>
  )
}

export default PlanTableMonthGroupCell
